import type { IVoyageCrew } from './voyageCrew'
import type { IVoyageSystemState } from './voyageSystems'
import { voyageSystems_battleRifleRack } from './voyageSystems_battleRifleRack-sidecar'
import { voyageSystems_coffeeMaker } from './voyageSystems_coffeeMaker-sidecar'
import { voyageSystems_crematorium } from './voyageSystems_crematorium-sidecar'
import { voyageSystems_cryo } from './voyageSystems_cryo-sidecar'
import { voyageSystems_externalSensor } from './voyageSystems_externalSensor-sidecar'
import { voyageSystems_internalSensor } from './voyageSystems_internalSensor-sidecar'
import { voyageSystems_laser } from './voyageSystems_laser-sidecar'
import { voyageSystems_lifeSupport } from './voyageSystems_lifeSupport-sidecar'
import { voyageSystems_med } from './voyageSystems_med-sidecar'
import { voyageSystems_medPad } from './voyageSystems_medPad-sidecar'
import { voyageSystems_reactor } from './voyageSystems_reactor-sidecar'
import { voyageSystems_torpedo } from './voyageSystems_torpedo-sidecar'
import { voyageSystems_torpedoLoader } from './voyageSystems_torpedoLoader-sidecar'
import { EVoyageTile } from './voyageTileData'
import type { IVoyageTilemapPart } from './voyageTilemapPart'

export interface IVoyageSystemData {
  startsWith: string
  tileType: EVoyageTile
  onCreate: (sys: IVoyageSystemState) => void
  crewUpdate: (obj: IVoyageCrew) => void
  onEnter?: (obj: IVoyageCrew) => void
  startsWithAmmo: number
  abbv: string
  abbvXAdjust: number
  infoText: string
  name: string
  extraInfo?: (t: IVoyageTilemapPart) => string
  frozenCrewName?: string
}

const _default: IVoyageSystemData = {
  name: 'Default',
  startsWith: 'default',
  tileType: EVoyageTile.System,
  onCreate: () => {},
  crewUpdate: () => {},
  startsWithAmmo: 0,
  abbv: '',
  abbvXAdjust: 0,
  infoText: '',
  frozenCrewName: '',
}

let dataList: IVoyageSystemData[] = []

export function register(data: Partial<IVoyageSystemData>) {
  let d = Object.assign({}, _default, data)
  dataList.push(d)
  return d
}

export function find(tile: IVoyageTilemapPart) {
  for (let i = 0; i < dataList.length; i++) {
    let c = dataList[i]
    if (c.tileType !== tile.tile3.type) {
      continue
    }
    if (c.startsWith) {
      if (!tile.text3.startsWith(c.startsWith)) {
        continue
      }
    }
    return c
  }

  return _default
}

export function registerAll() {
  register({
    startsWith: 'null',
    name: 'Null',
  })

  register({
    startsWith: 'cryo',
    onCreate: voyageSystems_cryo.onCreate,
    crewUpdate: voyageSystems_cryo.crewUpdate,
    startsWithAmmo: 1,
    abbv: 'Y',
    name: 'Cryo Freezing Station',
    infoText: 'Often contains emergency additional crew in a popsicle state.',
    extraInfo: (t) => {
      if (t.system) {
        if (t.system.ammo === 0) {
          return 'Is currently empty'
        }
        if (t.system.ammo === 1) {
          return 'Contains ' + t.system.frozenCrewName
        }
      }
      return '?'
    },
  })

  register({
    startsWith: 'sensor-internal',
    crewUpdate: voyageSystems_internalSensor.crewUpdate,
    abbv: 'SI',
    name: 'Internal Sensors',
    infoText: 'Scan for anomalies inside the ship.',
  })

  register({
    startsWith: 'sensor-external',
    crewUpdate: voyageSystems_externalSensor.crewUpdate,
    abbv: 'SE',
    name: 'External Sensors',
    infoText:
      'Scan for anomalies ouside the ship using radar, lidar, and truthdar.',
  })

  register({
    startsWith: 'med',
    crewUpdate: voyageSystems_med.crewUpdate,
    abbv: 'M',
    abbvXAdjust: -3,
    name: 'Medical Station',
    infoText: 'Enable healing on the nearby Medical Pad.',
  })

  register({
    tileType: EVoyageTile.MedPad,
    startsWith: 'pad',
    crewUpdate: voyageSystems_medPad.crewUpdate,
    name: 'Medical Pad',
    infoText:
      'Stand here to heal, if there is a crewmember operating the nearby Medical Station.',
  })

  register({
    tileType: EVoyageTile.Elevator,
    startsWith: '',
    //crewUpdate: voyageSystems_med.crewUpdate,
    name: 'Elevator',
    infoText: 'Moves crew between different floors on the Dawn Star.',
  })

  register({
    startsWith: 'reactor',
    crewUpdate: voyageSystems_reactor.crewUpdate,
    abbv: 'R',
    name: 'Reactor',
    infoText:
      'Powers the ship, a critical component that you do not want to go critical.',
  })

  register({
    startsWith: 'life-support',
    crewUpdate: voyageSystems_lifeSupport.crewUpdate,
    abbv: 'LS',
    name: 'Life Support',
    infoText:
      'Supports life by providing oxygen to fragile crew members. Does not help robots.',
  })

  register({
    startsWith: 'crematorium',
    crewUpdate: voyageSystems_crematorium.crewUpdate,
    abbv: 'CM',
    name: 'Crematorium',
    abbvXAdjust: -3,
    infoText:
      'In space, no one can hear you scream.\n\nTrying to find a safe way to dispose of dead bodies?\n\nIn space, no one can. Here, use crematorium.',
  })

  register({
    startsWith: 'laser',
    crewUpdate: voyageSystems_laser.crewUpdate,
    abbv: 'L',
    name: 'Laser Cannon',
    infoText:
      'Fast and easy to use laser cannon (tm). Beware, it takes quite a toll on the ship power.',
  })

  register({
    startsWith: 'torpedo',
    crewUpdate: voyageSystems_torpedo.crewUpdate,
    abbv: 'T',
    name: 'Torpedo Launcher',
    infoText:
      'Fire highly explosive torpedoes. Rumors about the ammunition being highly-refined potatoes are unfounded. Requires a crew to first load a torpedo in the nearby Torpedo Loader.',
  })

  register({
    startsWith: 'loader',
    crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    abbv: 'TL',
    name: 'Torpedo Loader',
    infoText:
      'Careful! These things are expensive and heavy. Load a highly-refined potato-based exposive into the nearby Torpedo Launcher tube.',
  })

  register({
    startsWith: 'bed',
    // crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    abbv: 'B',
    name: 'Bed',
    infoText:
      'If you do not know what one of these are for, you are probably a bad alien impostor.',
  })

  register({
    startsWith: 'captain',
    // crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    abbv: 'C',
    name: 'The Chair of the Captain',
    infoText: 'Does not do much but it sure looks comfortable.',
  })

  register({
    startsWith: 'galley',
    // crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    abbv: 'G',
  })

  register({
    startsWith: 'engine',
    // crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    abbv: 'E',
  })

  register({
    startsWith: 'shield',
    // crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    abbv: 'S',
  })

  register({
    startsWith: 'nav',
    // crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    abbv: 'N',
    abbvXAdjust: -2,
  })

  register({
    startsWith: 'loading',
    // crewUpdate: voyageSystems_torpedoLoader.crewUpdate,
    abbv: 'LB',
    name: 'Loading bay',
    infoText: '',
  })

  register({
    startsWith: 'coffe',
    onEnter: voyageSystems_coffeeMaker.onEnter,
    abbv: 'CF',
    name: 'Coffee Maker',

    infoText:
      'No self-respecting captain would leave on a multi-year journey without coffee!\n\nCoffee speeds up crewmembers who drink it.',
  })

  register({
    startsWith: 'battlerifle',
    // crewUpdate: voyageSystems_coffeeMaker.crewUpdate,
    onEnter: voyageSystems_battleRifleRack.onEnter,
    abbv: 'BR',
    name: 'Battle Rifle Rack',
    startsWithAmmo: 3,
    infoText: 'Battle Rifles - for when you want aliens dead faster (TM)',

    extraInfo: (t) => {
      if (t.system) {
        if (t.system.ammo === 0) {
          return 'Is currently empty'
        } else {
          return (
            'Contains ' +
            t.system.ammo +
            ' battle rifle' +
            (t.system.ammo === 1 ? '' : 's')
          )
        }
      }
      return '?'
    },
  })
}
